/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { linksDefinition, ProcessSummary, useProcessesContext } from 'ogc-processes-components';
import { classNames } from 'primereact/utils';
import { CarboneScore as CarboneScoreModel } from '@/types/models.ts';
import { CarboneScore } from '@/components/ProcessesWrapper/CarboneScore.tsx';
import { Utils } from '@/utils/utils.ts';

interface Props {
  process: ProcessSummary;
  className?: string;
  help?: boolean;
  showUnknown?: boolean;
}
export const ProcessCarboneScore: React.FC<Props> = ({ process, className, help, showUnknown }) => {
  const { processesEndpoint } = useProcessesContext();

  const { data, isPending } = useQuery({
    queryKey: ['carbone_score', process, processesEndpoint],
    queryFn: async () => {
      const self_link = process.links?.find((link) => link.rel === linksDefinition.process);
      if (self_link) {
        return processesEndpoint.get<CarboneScoreModel>(self_link.href.concat('/carbon'));
      }
    }
  });

  return (
    <div className={classNames(className, 'w-hull')}>
      {isPending ? (
        <span>load</span>
      ) : (
        data?.score && (
          <CarboneScore
            showUnknown={showUnknown}
            id={`process${Utils.simpleHashStringSync(process.id)}`}
            help={help}
            score={data?.score}
          />
        )
      )}
    </div>
  );
};
